import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  List, ListItem, Box, Typography, Alert, CircularProgress, Chip, Tooltip,
} from '@mui/material';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { describeError, getAll, getOrder, setOrder } from '../admin/api';

// Moves the item at `from` to index `to`, shifting the rest. Used by both the
// drag handler and the arrow buttons so they cannot drift apart.
function moveItem(list, from, to) {
  if (from === to || to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

const VideoOrderModal = ({ isOpen, onClose }) => {
  const [videoOrder, setVideoOrder] = useState([]);
  const [titles, setTitles] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // The catalog needs no auth, so fetch both and let the order call be the
      // one that reports an authentication failure.
      const [order, catalog] = await Promise.all([getOrder(), getAll()]);

      setTitles(
        Object.fromEntries(Object.entries(catalog).map(([id, v]) => [id, v?.title || ''])),
      );
      setVideoOrder(order);
      setDirty(false);
    } catch (err) {
      setVideoOrder([]);
      setError(describeError(err, 'The order could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const apply = (next) => {
    if (next === videoOrder) return;
    setVideoOrder(next);
    setDirty(true);
  };

  // The server appends newly added videos to the order but never prunes deleted
  // ones, so an id with no catalog entry is a leftover. Saving without it is
  // what removes it.
  const isStale = (id) => Object.keys(titles).length > 0 && !(id in titles);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await setOrder(videoOrder);
      onClose();
    } catch (err) {
      setError(describeError(err, 'Nothing was saved.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = (index) => {
    if (dragIndex !== null) apply(moveItem(videoOrder, dragIndex, index));
    setDragIndex(null);
  };

  const staleCount = videoOrder.filter(isStale).length;

  return (
    <Dialog open={isOpen} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Video order
        <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'none', letterSpacing: 0 }}>
          Drag a row to move it, or nudge it with the arrows. {videoOrder.length} videos.
          Owned videos are still hoisted above the rest on the home page.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={<Button size="small" onClick={load} disabled={loading}>Retry</Button>}
          >
            {error}
          </Alert>
        )}
        {staleCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {staleCount} entr{staleCount === 1 ? 'y is' : 'ies are'} no longer in the catalog.
            Remove {staleCount === 1 ? 'it' : 'them'} and save to prune.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <List dense disablePadding>
            {videoOrder.map((videoId, index) => (
              <ListItem
                key={videoId}
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(index)}
                onDragEnd={() => setDragIndex(null)}
                sx={{
                  bgcolor: dragIndex === index ? 'action.selected' : 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 0.5,
                  cursor: 'grab',
                  opacity: dragIndex !== null && dragIndex !== index ? 0.6 : 1,
                }}
                secondaryAction={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="Move to top">
                      {/* span keeps the tooltip working while the button is disabled */}
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Move to top"
                          disabled={index === 0}
                          onClick={() => apply(moveItem(videoOrder, index, 0))}
                        >
                          <VerticalAlignTopIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton
                      size="small"
                      aria-label="Move up"
                      disabled={index === 0}
                      onClick={() => apply(moveItem(videoOrder, index, index - 1))}
                    >
                      <ArrowUpwardIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Move down"
                      disabled={index === videoOrder.length - 1}
                      onClick={() => apply(moveItem(videoOrder, index, index + 1))}
                    >
                      <ArrowDownwardIcon fontSize="inherit" />
                    </IconButton>
                    {isStale(videoId) && (
                      <Tooltip title="Remove this leftover entry">
                        <IconButton
                          size="small"
                          aria-label="Remove leftover entry"
                          onClick={() => apply(videoOrder.filter((_, i) => i !== index))}
                        >
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                }
              >
                <DragIndicatorIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />
                <Typography variant="body2" sx={{ width: 32, color: 'text.secondary' }}>
                  {index + 1}
                </Typography>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap>
                    {titles[videoId] || videoId}
                  </Typography>
                  {titles[videoId] && (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {videoId}
                    </Typography>
                  )}
                </Box>
                {isStale(videoId) && <Chip size="small" color="warning" label="not in catalog" sx={{ mr: 1 }} />}
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
          {dirty ? 'Unsaved changes' : ''}
        </Typography>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading || !dirty}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoOrderModal;
